import { useReveal } from "../../lib/hooks";

/**
 * House of Unity — a compact section near the end of the page. Most Veda Union
 * activity is online or at events around Europe and the world; this is the one
 * physical home base, given a brief, contained section so it doesn't dominate.
 *
 * Layout: text on left (so the loaded text fills the visible space immediately),
 * single framed image on right.
 */
export function HouseOfUnity() {
  const { ref: textRef } = useReveal<HTMLDivElement>();
  const { ref: imgRef } = useReveal<HTMLDivElement>();

  return (
    <section
      id="house"
      className="py-20 md:py-28 border-y border-[var(--color-rule)] bg-[var(--color-vellum-dim)]"
    >
      <div className="container-edit">
        <div className="section-heading">
          <span className="section-heading-num tabular">06 / House of Unity</span>
          <hr className="rule flex-1 hidden sm:block" />
          <span className="font-display-italic text-[0.95rem] text-[var(--color-ink-mute)] hidden md:inline whitespace-nowrap">
            Krnjak, Croatia
          </span>
        </div>

        <div className="grid grid-cols-12 gap-y-12 lg:gap-x-16 items-center">
          {/* Text — left */}
          <div ref={textRef} className="col-span-12 lg:col-span-6 reveal">
            <h2 className="font-display text-[clamp(2rem,4.5vw,3.4rem)] leading-[1.05] tracking-[-0.02em]">
              Mostly we meet online —{" "}
              <span className="font-display-italic" style={{ color: "var(--color-violet)" }}>
                sometimes in person
              </span>
              .
            </h2>
            <hr className="rule-violet w-12 mt-7 mb-7" />
            <p className="text-[1.05rem] leading-[1.7] text-[var(--color-ink-soft)]">
              Most of our gathering happens online, and through events scattered across
              Europe and beyond. But there is also a small wooden house in the rolling
              hills of central Croatia, near the village of Krnjak — the House of Unity.
              It's our physical home base, where many of the in-person chanting circles,
              study sessions, workshops, yajñas, pūjās, and bhajan evenings take place.
            </p>
          </div>

          {/* Image — right, single, modest */}
          <div ref={imgRef} className="col-span-12 lg:col-span-6 reveal">
            <figure>
              <div
                className="image-frame aspect-[4/3]"
                style={{ outline: "1px solid var(--color-rule-violet)", outlineOffset: "8px" }}
              >
                <img
                  src="/photos/house-of-unity-evening.jpg"
                  alt="The House of Unity, with members of the community gathered at the entrance"
                  loading="lazy"
                />
              </div>
              <figcaption className="mt-9 flex items-baseline gap-3 text-[0.72rem] tracking-[0.18em] uppercase text-[var(--color-ink-mute)] font-grotesk">
                <span>Plate</span>
                <span className="tabular" style={{ color: "var(--color-violet)" }}>
                  v.
                </span>
                <span>House of Unity, Krnjak</span>
              </figcaption>
            </figure>
          </div>
        </div>
      </div>
    </section>
  );
}
